import React, { useState, useEffect, useCallback } from 'react';
import csvImportApiClient from '../../utilities/csvImportApiClient';
import SearchableSelect from '../SearchableSelect';
import CustomerSelector from '../CustomerSelector';
import AddressSelector from '../AddressSelector';
import Styles from './styles.module.css';

const ProfileEditor = ({ profile, isCreating, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        profileName: '',
        importType: 'single_customer_multiple_orders',
        description: '',
        salesChannel: '',
        paymentMethod: '',
        shippingMethod: '',
        withPrices: true,
        forceTaxFree: false,
        strictProductValidation: true, // IMPROVEMENT: Add strict validation setting
        priceColumn: '',
        mapping: {},
        config: {},
        countryShippingMethods: []
    });

    // Customer and address states
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
    const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);

    const [salesChannels, setSalesChannels] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [shippingMethods, setShippingMethods] = useState([]);
    const [countries, setCountries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingSalesChannels, setIsLoadingSalesChannels] = useState(false);
    const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
    const [isLoadingShippingMethods, setIsLoadingShippingMethods] = useState(false);
    const [isLoadingCountries, setIsLoadingCountries] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [csvValidationResults, setCsvValidationResults] = useState(null);
    const [activeFieldTab, setActiveFieldTab] = useState('assigned'); // 'assigned' or 'unassigned'

    const importTypes = [
        {
            value: 'single_customer_multiple_orders',
            label: 'Ein Kunde, mehrere Bestellungen',
            description: 'Importiert CSV-Zeilen als separate Bestellungen für einen ausgewählten Kunden'
        },
        {
            value: 'single_customer_single_order',
            label: 'Ein Kunde, eine Bestellung',
            description: 'Kombiniert alle CSV-Zeilen zu einer Bestellung für den ausgewählten Kunden'
        },
        {
            value: 'multiple_customers_multiple_orders',
            label: 'Mehrere Kunden, mehrere Bestellungen',
            description: 'Verwendet Kunden- und Adressdaten aus der CSV-Datei'
        }
    ];

    // Standard Shopware 6 Order-Felder für Mapping
    const orderFields = [
        // Bestellkopf-Felder
        { category: 'Bestellung', value: 'orderNumber', label: 'Bestellnummer' },
        { category: 'Bestellung', value: 'orderDateTime', label: 'Bestelldatum' },
        { category: 'Bestellung', value: 'orderDate', label: 'Bestelldatum (nur Datum)' },
        { category: 'Bestellung', value: 'orderTime', label: 'Bestellzeit' },
        { category: 'Bestellung', value: 'customerComment', label: 'Kundenkommentar' },
        { category: 'Bestellung', value: 'internalComment', label: 'Interner Kommentar' },
        { category: 'Bestellung', value: 'affiliateCode', label: 'Affiliate Code' },
        { category: 'Bestellung', value: 'campaignCode', label: 'Kampagnen Code' },
        
        // Positions-Felder
        { category: 'Position', value: 'lineItems.product.productNumber', label: 'Produktnummer (SKU)' },
        { category: 'Position', value: 'lineItems.product.name', label: 'Produktname' },
        { category: 'Position', value: 'lineItems.quantity', label: 'Menge' },
        { category: 'Position', value: 'lineItems.label', label: 'Positionsbezeichnung' },
        { category: 'Position', value: 'lineItems.description', label: 'Positionsbeschreibung' },
        
        // Kunden-Felder
        { category: 'Kunde', value: 'orderCustomer.firstName', label: 'Vorname (Kunde)' },
        { category: 'Kunde', value: 'orderCustomer.lastName', label: 'Nachname (Kunde)' },
        { category: 'Kunde', value: 'orderCustomer.email', label: 'E-Mail (Kunde)' },
        { category: 'Kunde', value: 'orderCustomer.customerNumber', label: 'Kundennummer' },
        { category: 'Kunde', value: 'orderCustomer.company', label: 'Firma (Kunde)' },
        { category: 'Kunde', value: 'orderCustomer.title', label: 'Anrede (Kunde)' },
        { category: 'Kunde', value: 'orderCustomer.salutation', label: 'Anrede (Kunde)' },
        { category: 'Kunde', value: 'orderCustomer.vatIds', label: 'USt-IdNr.' },
        
        // Rechnungsadresse
        { category: 'Rechnungsadresse', value: 'billingAddress.firstName', label: 'Vorname (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.lastName', label: 'Nachname (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.company', label: 'Firma (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.street', label: 'Straße (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.additionalAddressLine1', label: 'Adresszusatz 1 (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.additionalAddressLine2', label: 'Adresszusatz 2 (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.zipcode', label: 'PLZ (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.city', label: 'Stadt (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.phoneNumber', label: 'Telefon (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.country.name', label: 'Land (Rechnung)' },
        { category: 'Rechnungsadresse', value: 'billingAddress.country.iso', label: 'Ländercode (Rechnung)' },
        
        // Lieferadresse
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.firstName', label: 'Vorname (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.lastName', label: 'Nachname (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.company', label: 'Firma (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.street', label: 'Straße (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.additionalAddressLine1', label: 'Adresszusatz 1 (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.additionalAddressLine2', label: 'Adresszusatz 2 (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.zipcode', label: 'PLZ (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.city', label: 'Stadt (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.phoneNumber', label: 'Telefon (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.country.name', label: 'Land (Versand)' },
        { category: 'Lieferadresse', value: 'deliveries.shippingOrderAddress.country.iso', label: 'Ländercode (Versand)' },
        
        // Lieferung & Zahlung
        { category: 'Lieferung', value: 'deliveries.trackingCodes', label: 'Tracking-Codes' },
        { category: 'Lieferung', value: 'deliveries.shippingMethod.name', label: 'Versandart' },
        { category: 'Lieferung', value: 'deliveries.shippingCosts.totalPrice', label: 'Versandkosten' },
        { category: 'Lieferung', value: 'deliveries.shippingDateEarliest', label: 'Lieferdatum frühestens' },
        { category: 'Lieferung', value: 'deliveries.shippingDateLatest', label: 'Lieferdatum spätestens' },
        
        // Sonstige
        { category: 'Sonstige', value: 'customFields', label: 'Benutzerdefinierte Felder (JSON)' },
        { category: 'Sonstige', value: 'tags', label: 'Tags' },
        { category: 'Sonstige', value: 'deepLinkCode', label: 'Deep-Link Code' }
    ];

    const loadFormData = useCallback(() => {
        if (profile && !isCreating) {
            console.log('[ProfileEditor] Loading profile data:', profile);
            console.log('[ProfileEditor] Profile fieldMapping:', profile.fieldMapping);
            
            // Handle legacy import types by mapping them to new format
            let mappedImportType = profile.importType || 'single_customer_multiple_orders';
            let withPrices = profile.defaultValues?.withPrices !== false;
            
            // Map old import types to new format
            if (profile.importType === 'single_customer_single_order_with_prices') {
                mappedImportType = 'single_customer_single_order';
                withPrices = true;
            } else if (profile.importType === 'single_customer_single_order_without_prices') {
                mappedImportType = 'single_customer_single_order';
                withPrices = false;
            }
            
            setFormData({
                profileName: profile.name || '',
                importType: mappedImportType,
                description: profile.description || '',
                salesChannel: profile.defaultValues?.salesChannelId || '',
                paymentMethod: profile.defaultValues?.paymentMethodId || '',
                shippingMethod: profile.defaultValues?.shippingMethodId || '',
                withPrices: withPrices,
                forceTaxFree: profile.defaultValues?.forceTaxFree || false,
                strictProductValidation: profile.defaultValues?.strictProductValidation !== false, // Default to true
                priceColumn: profile.defaultValues?.priceColumn || '',
                mapping: profile.fieldMapping || {},
                config: profile.config || {},
                countryShippingMethods: profile.countryShippingMethods || []
            });
            
            console.log('[ProfileEditor] Set mapping to formData:', profile.fieldMapping || {});
            
            // Load customer and address data if available
            if (profile.customer) {
                setSelectedCustomer(profile.customer);
                if (profile.billingAddress) {
                    setSelectedBillingAddress(profile.billingAddress);
                }
                if (profile.shippingAddress) {
                    setSelectedShippingAddress(profile.shippingAddress);
                }
                // Set addresses array for the AddressSelector - deduplicate by ID
                const uniqueAddresses = [];
                const addedIds = new Set();
                
                [profile.billingAddress, profile.shippingAddress].filter(Boolean).forEach(address => {
                    if (address && !addedIds.has(address.id)) {
                        uniqueAddresses.push(address);
                        addedIds.add(address.id);
                    }
                });
                
                setAddresses(uniqueAddresses);
            }
            
            // Load saved CSV headers if available (so user doesn't need to re-upload CSV)
            if (profile.csvHeaders && Array.isArray(profile.csvHeaders)) {
                setCsvHeaders(profile.csvHeaders);
                console.log('[ProfileEditor] Loaded saved CSV headers:', profile.csvHeaders);
            }
        }
    }, [profile, isCreating]);

    useEffect(() => {
        loadFormData();
        loadSalesChannels();
        loadPaymentMethods();
        loadShippingMethods();
        loadCountries();
    }, [profile, loadFormData]);

    const loadSalesChannels = async () => {
        setIsLoadingSalesChannels(true);
        try {
            const response = await csvImportApiClient.getSalesChannels();
            if (response.success) {
                setSalesChannels(response.data || []);
            } else {
                console.error('Error loading sales channels:', response.error);
                setSalesChannels([]);
            }
        } catch (error) {
            console.error('Error loading sales channels:', error);
            setSalesChannels([]);
        } finally {
            setIsLoadingSalesChannels(false);
        }
    };

    const loadPaymentMethods = async () => {
        setIsLoadingPaymentMethods(true);
        try {
            console.log('[ProfileEditor] Loading payment methods...');
            const response = await csvImportApiClient.getPaymentMethods();
            console.log('[ProfileEditor] Payment methods response:', response);
            
            if (response.success) {
                console.log('[ProfileEditor] Payment methods loaded:', response.data?.length || 0);
                setPaymentMethods(response.data || []);
            } else {
                console.error('Error loading payment methods:', response.error);
                setPaymentMethods([]);
            }
        } catch (error) {
            console.error('Error loading payment methods:', error);
            setPaymentMethods([]);
        } finally {
            setIsLoadingPaymentMethods(false);
        }
    };

    const loadShippingMethods = async () => {
        setIsLoadingShippingMethods(true);
        try {
            console.log('[ProfileEditor] Loading shipping methods...');
            const response = await csvImportApiClient.getShippingMethods();
            console.log('[ProfileEditor] Shipping methods response:', response);
            
            if (response.success) {
                console.log('[ProfileEditor] Shipping methods loaded:', response.data?.length || 0);
                setShippingMethods(response.data || []);
            } else {
                console.error('Error loading shipping methods:', response.error);
                setShippingMethods([]);
            }
        } catch (error) {
            console.error('Error loading shipping methods:', error);
            setShippingMethods([]);
        } finally {
            setIsLoadingShippingMethods(false);
        }
    };

    const loadCountries = async () => {
        setIsLoadingCountries(true);
        try {
            console.log('[ProfileEditor] Loading countries...');
            const response = await csvImportApiClient.getCountries();
            console.log('[ProfileEditor] Countries response:', response);
            
            if (response.success) {
                console.log('[ProfileEditor] Countries loaded:', response.data?.length || 0);
                setCountries(response.data || []);
            } else {
                console.error('Error loading countries:', response.error);
                setCountries([]);
            }
        } catch (error) {
            console.error('Error loading countries:', error);
            setCountries([]);
        } finally {
            setIsLoadingCountries(false);
        }
    };

    // Customer and address handlers
    const handleCustomerSearch = useCallback(async (searchTerm) => {
        setIsLoadingCustomers(true);
        try {
            const response = await csvImportApiClient.getCustomers(searchTerm);
            if (response.success) {
                setCustomers(response.data || []);
            } else {
                console.error('Customer search failed:', response.error);
                setCustomers([]);
            }
        } catch (error) {
            console.error('Error searching customers:', error);
            setCustomers([]);
        } finally {
            setIsLoadingCustomers(false);
        }
    }, []);

    const handleCustomerSelect = async (customer) => {
        setSelectedBillingAddress(null);
        setSelectedShippingAddress(null);
        setAddresses([]);
        
        // Fetch full customer details with group information
        if (customer?.id) {
            setIsLoadingAddresses(true);
            try {
                // Load customer details with group information
                const customerDetails = await csvImportApiClient.getCustomerDetails(customer.id);
                console.log('[ProfileEditor] Fetched customer details:', customerDetails);
                
                // Set the customer with full details
                setSelectedCustomer(customerDetails || customer);
                
                // Load addresses
                const customerAddresses = await csvImportApiClient.getCustomerAddresses(customer.id);
                console.log('[ProfileEditor] Fetched addresses:', customerAddresses);
                
                if (Array.isArray(customerAddresses)) {
                    setAddresses(customerAddresses);
                    // Auto-select default addresses if available
                    const defaultBilling = customerAddresses.find(addr => addr.id === (customerDetails?.defaultBillingAddressId || customer.defaultBillingAddressId));
                    const defaultShipping = customerAddresses.find(addr => addr.id === (customerDetails?.defaultShippingAddressId || customer.defaultShippingAddressId));
                    if (defaultBilling) setSelectedBillingAddress(defaultBilling);
                    if (defaultShipping) setSelectedShippingAddress(defaultShipping);
                } else {
                    console.error('[ProfileEditor] Invalid address format received:', customerAddresses);
                    setAddresses([]);
                }
            } catch (error) {
                console.error('Error fetching customer details:', error);
                // Fallback to basic customer info
                setSelectedCustomer(customer);
                setAddresses([]);
            } finally {
                setIsLoadingAddresses(false);
            }
        } else {
            setSelectedCustomer(customer);
        }
    };

    const handleBillingAddressSelect = (address) => {
        setSelectedBillingAddress(address);
    };

    const handleShippingAddressSelect = (address) => {
        setSelectedShippingAddress(address);
    };

    // Country-specific shipping handlers
    const handleCountryShippingChange = (index, field, value) => {
        setFormData(prev => {
            const newCountryShippingMethods = [...(prev.countryShippingMethods || [])];
            newCountryShippingMethods[index] = {
                ...newCountryShippingMethods[index],
                [field]: value
            };
            return {
                ...prev,
                countryShippingMethods: newCountryShippingMethods
            };
        });
    };

    const addCountryShipping = () => {
        setFormData(prev => ({
            ...prev,
            countryShippingMethods: [
                ...(prev.countryShippingMethods || []),
                { countryId: '', shippingMethodId: '' }
            ]
        }));
    };

    const removeCountryShipping = (index) => {
        setFormData(prev => ({
            ...prev,
            countryShippingMethods: (prev.countryShippingMethods || []).filter((_, i) => i !== index)
        }));
    };

    const handleCsvUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            alert('Bitte wählen Sie eine CSV-Datei aus.');
            return;
        }

        setCsvFile(file);
        setIsLoading(true);

        try {
            const response = await csvImportApiClient.parseCSVHeader(file);
            if (response.success) {
                setCsvHeaders(response.data.headers || []);
                setCsvValidationResults(response.data.validationResults || null);
                console.log('CSV Headers loaded:', response.data.headers);
                console.log('CSV Validation results:', response.data.validationResults);
                
                // Show validation results if there are warnings or errors
                if (response.data.validationResults) {
                    const { errors, warnings } = response.data.validationResults;
                    if (errors.length > 0) {
                        alert('❌ Kritische Fehler in der CSV-Datei gefunden:\n' + errors.join('\n'));
                    } else if (warnings.length > 0) {
                        const shouldContinue = window.confirm(
                            `⚠️ Warnungen in der CSV-Datei gefunden:\n${warnings.slice(0, 5).join('\n')}${warnings.length > 5 ? `\n... und ${warnings.length - 5} weitere` : ''}\n\nMöchten Sie trotzdem fortfahren?`
                        );
                        if (!shouldContinue) {
                            setCsvFile(null);
                            setCsvHeaders([]);
                            setCsvValidationResults(null);
                            return;
                        }
                    }
                }
            } else {
                console.error('Error parsing CSV:', response.error);
                setCsvHeaders([]);
                setCsvValidationResults(null);
                alert('Fehler beim Parsen der CSV-Datei: ' + (response.error || 'Unbekannter Fehler'));
            }
        } catch (error) {
            console.error('Error uploading CSV:', error);
            setCsvHeaders([]);
            setCsvValidationResults(null);
            alert('Fehler beim Hochladen der CSV-Datei: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const removeCsvFile = () => {
        setCsvFile(null);
        setCsvValidationResults(null);
        // When editing existing profile, keep saved headers and mapping
        if (!isCreating && profile?.csvHeaders) {
            setCsvHeaders(profile.csvHeaders);
            // Keep existing mapping
        } else {
            // Only clear everything for new profiles
            setCsvHeaders([]);
            setFormData(prev => ({
                ...prev,
                mapping: {}
            }));
        }
    };


    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMappingChange = (csvColumn, shopwareField) => {
        setFormData(prev => ({
            ...prev,
            mapping: {
                ...prev.mapping,
                [csvColumn]: shopwareField
            }
        }));
    };

    const handleSave = async () => {
        if (!formData.profileName.trim()) {
            alert('Bitte geben Sie einen Profilnamen ein.');
            return;
        }

        // Only require CSV and mapping for new profiles, not when editing existing ones
        if (!isCreating) {
            // When editing existing profile, CSV upload is optional
            // If CSV is uploaded, require at least one mapping
            if (csvHeaders.length > 0) {
                const mappedFields = Object.values(formData.mapping).filter(field => field).length;
                if (mappedFields === 0) {
                    alert('Wenn Sie eine CSV-Datei hochladen, müssen Sie mindestens ein CSV-Feld einem Shopware-Feld zuordnen.');
                    return;
                }
            }
        } else {
            // For new profiles, require CSV and mappings
            if (csvHeaders.length === 0) {
                alert('Bitte laden Sie eine CSV-Datei hoch, um die Feld-Zuordnung zu konfigurieren.');
                return;
            }

            const mappedFields = Object.values(formData.mapping).filter(field => field).length;
            if (mappedFields === 0) {
                alert('Bitte ordnen Sie mindestens ein CSV-Feld einem Shopware-Feld zu.');
                return;
            }
        }

        setIsLoading(true);
        try {
            const profileData = {
                name: formData.profileName,
                description: formData.description,
                importType: formData.importType,
                fieldMapping: formData.mapping,
                csvHeaders: csvHeaders.length > 0 ? csvHeaders : undefined, // Save CSV headers for future use
                defaultValues: {
                    salesChannelId: formData.salesChannel,
                    paymentMethodId: formData.paymentMethod,
                    shippingMethodId: formData.shippingMethod,
                    withPrices: formData.withPrices,
                    forceTaxFree: formData.forceTaxFree,
                    strictProductValidation: formData.strictProductValidation, // IMPROVEMENT: Save validation setting
                    priceColumn: formData.priceColumn
                },
                // Add customer and address data if selected
                customer: selectedCustomer,
                billingAddress: selectedBillingAddress,
                shippingAddress: selectedShippingAddress,
                countryShippingMethods: formData.countryShippingMethods || [],
                active: true
            };

            await onSave(profileData);
        } catch (error) {
            console.error('Error saving profile:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getSelectedImportType = () => {
        return importTypes.find(type => type.value === formData.importType);
    };

    return (
        <div className={Styles.container}>
            <div className={Styles.header}>
                <h2>
                    {isCreating ? 'Neues Import-Profil erstellen' : 'Import-Profil bearbeiten'}
                </h2>
                <button className={Styles.backButton} onClick={onCancel}>
                    ← Zurück zur Übersicht
                </button>
            </div>

            <div className={Styles.form}>
                {/* Basic Profile Information */}
                <div className={Styles.section}>
                    <h3>Grundeinstellungen</h3>
                    
                    <div className={Styles.formGroup}>
                        <label>Profilname *</label>
                        <input
                            type="text"
                            value={formData.profileName}
                            onChange={(e) => handleInputChange('profileName', e.target.value)}
                            placeholder="z.B. Westwing Import"
                            className={Styles.input}
                        />
                    </div>

                    <div className={Styles.formGroup}>
                        <label>Beschreibung</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Optionale Beschreibung des Profils"
                            className={Styles.textarea}
                            rows={3}
                        />
                    </div>

                    <div className={Styles.formGroup}>
                        <label>Import-Typ *</label>
                        <select
                            value={formData.importType}
                            onChange={(e) => handleInputChange('importType', e.target.value)}
                            className={Styles.select}
                        >
                            {importTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                        {getSelectedImportType() && (
                            <p className={Styles.helpText}>
                                {getSelectedImportType().description}
                            </p>
                        )}
                    </div>

                    <div className={Styles.formGroup}>
                        <label>Preisbehandlung</label>
                        <div className={Styles.radioGroup}>
                            <label className={Styles.radioOption}>
                                <input
                                    type="radio"
                                    name="withPrices"
                                    value="true"
                                    checked={formData.withPrices === true}
                                    onChange={(e) => handleInputChange('withPrices', true)}
                                    className={Styles.radioInput}
                                />
                                <span className={Styles.radioLabel}>System-Preise verwenden</span>
                                <span className={Styles.radioDescription}>
                                    Produktpreise werden aus dem Shopware-Katalog übernommen<br/>
                                    <em style={{fontSize: '0.85em', color: '#666'}}>
                                        Fallback-Rule: 018ad19c51e572e4a6ad9fab218a04b8 (Shopkunden)
                                    </em>
                                </span>
                            </label>
                            <label className={Styles.radioOption}>
                                <input
                                    type="radio"
                                    name="withPrices"
                                    value="false"
                                    checked={formData.withPrices === false}
                                    onChange={(e) => handleInputChange('withPrices', false)}
                                    className={Styles.radioInput}
                                />
                                <span className={Styles.radioLabel}>Null-Preise (0 €)</span>
                                <span className={Styles.radioDescription}>Alle Positionen werden mit 0 € Preis importiert</span>
                            </label>
                        </div>
                        
                        {/* Tax-Free Checkbox - only visible when System Prices are enabled */}
                        {formData.withPrices && (
                            <div style={{
                                marginTop: '1.5rem', 
                                padding: '1rem', 
                                borderTop: '1px solid #e2e8f0',
                                backgroundColor: '#f8fafc'
                            }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '0.75rem',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.forceTaxFree}
                                        onChange={(e) => handleInputChange('forceTaxFree', e.target.checked)}
                                        style={{
                                            marginTop: '0.2rem',
                                            flexShrink: 0
                                        }}
                                    />
                                    <div>
                                        <div style={{
                                            fontWeight: '500',
                                            fontSize: '0.95rem',
                                            marginBottom: '0.5rem'
                                        }}>
                                            EU Lieferung B2B ohne Steuer (reverse charge)
                                        </div>
                                        <div style={{
                                            fontSize: '0.85rem',
                                            color: '#64748b',
                                            lineHeight: '1.4'
                                        }}>
                                            Bestellungen werden als steuerfrei markiert (taxStatus: "tax-free") unabhängig von der Kunden-USt-ID.
                                        </div>
                                        <div style={{
                                            fontSize: '0.8rem',
                                            color: '#94a3b8',
                                            marginTop: '0.25rem',
                                            fontStyle: 'italic'
                                        }}>
                                            Überschreibt die automatische Mehrwertsteuer-Erkennung. Für EU-Kunden mit gültiger USt-ID (Reverse-Charge-Verfahren).
                                        </div>
                                    </div>
                                </label>
                            </div>
                        )}
                        
                        {/* IMPROVEMENT: Strict Product Validation Setting */}
                        <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f1f5f9', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.strictProductValidation}
                                    onChange={(e) => handleInputChange('strictProductValidation', e.target.checked)}
                                    style={{
                                        marginTop: '0.2rem',
                                        flexShrink: 0
                                    }}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontWeight: '600',
                                        color: '#1e293b',
                                        marginBottom: '0.25rem'
                                    }}>
                                        🔒 Strikte Produktvalidierung
                                    </div>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#475569',
                                        lineHeight: '1.4'
                                    }}>
                                        Import wird abgebrochen, wenn nicht alle Produkte aus der CSV-Datei in Shopware gefunden werden.
                                    </div>
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: '#94a3b8',
                                        marginTop: '0.25rem',
                                        fontStyle: 'italic'
                                    }}>
                                        {formData.strictProductValidation ? 
                                            'Aktiviert: Fehlende Produkte führen zu Import-Fehlern. Empfohlen für Datenintegrität.' :
                                            'Deaktiviert: Fehlende Produkte werden übersprungen. Import wird mit verfügbaren Produkten fortgesetzt.'
                                        }
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Customer and Address Assignment */}
                <div className={Styles.section}>
                    <h3>Kunde & Adressen zuordnen</h3>
                    <p className={Styles.sectionDescription}>
                        Ordnen Sie diesem Profil einen Kunden und dessen Adressen zu. Diese werden dann automatisch beim Import verwendet.
                    </p>
                    
                    <div className={Styles.customerSection}>
                        <CustomerSelector
                            customers={customers}
                            selectedCustomer={selectedCustomer}
                            onCustomerSelect={handleCustomerSelect}
                            onCustomerSearch={handleCustomerSearch}
                            isLoading={isLoadingCustomers}
                            salesChannels={salesChannels}
                        />
                    </div>

                    {selectedCustomer && (
                        <div className={Styles.addressSection}>
                            <AddressSelector
                                addresses={addresses}
                                selectedBillingAddress={selectedBillingAddress}
                                selectedShippingAddress={selectedShippingAddress}
                                onBillingAddressSelect={handleBillingAddressSelect}
                                onShippingAddressSelect={handleShippingAddressSelect}
                                isLoading={isLoadingAddresses}
                                customer={selectedCustomer}
                            />
                        </div>
                    )}
                </div>

                {/* Channel, Payment and Shipping Settings */}
                <div className={Styles.section}>
                    <h3>Verkaufskanal, Zahlung & Versand</h3>
                    
                    <div className={Styles.formRow}>
                        <div className={Styles.formGroup}>
                            <label>Verkaufskanal</label>
                            <SearchableSelect
                                options={[
                                    { id: '', name: 'Standard verwenden' },
                                    ...salesChannels
                                ]}
                                value={formData.salesChannel}
                                onChange={(value) => handleInputChange('salesChannel', value)}
                                placeholder="Verkaufskanal auswählen"
                                searchPlaceholder="Verkaufskanal suchen..."
                                isLoading={isLoadingSalesChannels}
                                className={Styles.searchableSelect}
                                renderOption={(option) => (
                                    <div className={Styles.selectOption}>
                                        <span className={Styles.optionName}>{option.name}</span>
                                        {option.id && (
                                            <span className={Styles.optionId}>ID: {option.id}</span>
                                        )}
                                    </div>
                                )}
                            />
                        </div>

                        <div className={Styles.formGroup}>
                            <label>Zahlungsart</label>
                            <SearchableSelect
                                options={[
                                    { id: '', name: 'Standard verwenden' },
                                    ...paymentMethods
                                ]}
                                value={formData.paymentMethod}
                                onChange={(value) => handleInputChange('paymentMethod', value)}
                                placeholder="Zahlungsart auswählen"
                                searchPlaceholder="Zahlungsart suchen..."
                                isLoading={isLoadingPaymentMethods}
                                className={Styles.searchableSelect}
                                renderOption={(option) => (
                                    <div className={Styles.selectOption}>
                                        <span className={Styles.optionName}>{option.name}</span>
                                        {option.id && (
                                            <span className={Styles.optionId}>ID: {option.id}</span>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                    </div>

                    <div className={Styles.formRow}>
                        <div className={Styles.formGroup}>
                            <label>Standard-Versandart</label>
                            <SearchableSelect
                                options={[
                                    { id: '', name: 'Standard verwenden' },
                                    ...shippingMethods
                                ]}
                                value={formData.shippingMethod}
                                onChange={(value) => handleInputChange('shippingMethod', value)}
                                placeholder="Versandart auswählen"
                                searchPlaceholder="Versandart suchen..."
                                isLoading={isLoadingShippingMethods}
                                className={Styles.searchableSelect}
                                renderOption={(option) => (
                                    <div className={Styles.selectOption}>
                                        <span className={Styles.optionName}>{option.name}</span>
                                        {option.id && (
                                            <span className={Styles.optionId}>ID: {option.id}</span>
                                        )}
                                    </div>
                                )}
                            />
                            <p className={Styles.helpText}>
                                Diese Versandart wird verwendet, wenn keine länderspezifische Konfiguration vorhanden ist.
                            </p>
                        </div>

                        <div className={Styles.formGroup}>
                            {/* Empty div for layout balance */}
                        </div>
                    </div>

                    {/* Country-Specific Shipping Configuration */}
                    <div className={Styles.countryShippingSection}>
                        <h4>Länderspezifische Versandarten</h4>
                        <p className={Styles.sectionDescription}>
                            Konfigurieren Sie spezielle Versandarten für bestimmte Länder. Diese überschreiben die Standard-Versandart.
                        </p>
                        
                        {formData.countryShippingMethods && formData.countryShippingMethods.map((countryShipping, index) => (
                            <div key={index} className={Styles.countryShippingRow}>
                                <div className={Styles.countryShippingFields}>
                                    <div className={Styles.formGroup}>
                                        <label>Land</label>
                                        <SearchableSelect
                                            options={countries}
                                            value={countryShipping.countryId}
                                            onChange={(value) => handleCountryShippingChange(index, 'countryId', value)}
                                            placeholder="Land auswählen"
                                            searchPlaceholder="Land suchen..."
                                            isLoading={isLoadingCountries}
                                            className={Styles.searchableSelect}
                                            renderOption={(option) => (
                                                <div className={Styles.selectOption}>
                                                    <span className={Styles.optionName}>{option.name}</span>
                                                    <span className={Styles.optionId}>{option.iso}</span>
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div className={Styles.formGroup}>
                                        <label>Versandart</label>
                                        <SearchableSelect
                                            options={shippingMethods}
                                            value={countryShipping.shippingMethodId}
                                            onChange={(value) => handleCountryShippingChange(index, 'shippingMethodId', value)}
                                            placeholder="Versandart auswählen"
                                            searchPlaceholder="Versandart suchen..."
                                            isLoading={isLoadingShippingMethods}
                                            className={Styles.searchableSelect}
                                            renderOption={(option) => (
                                                <div className={Styles.selectOption}>
                                                    <span className={Styles.optionName}>{option.name}</span>
                                                    {option.id && (
                                                        <span className={Styles.optionId}>ID: {option.id}</span>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeCountryShipping(index)}
                                    className={Styles.removeCountryShippingButton}
                                    title="Länderspezifische Versandart entfernen"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                        
                        <button
                            type="button"
                            onClick={addCountryShipping}
                            className={Styles.addCountryShippingButton}
                        >
                            + Länderspezifische Versandart hinzufügen
                        </button>
                    </div>
                </div>

                {/* CSV Mapping */}
                <div className={Styles.section}>
                    <h3>CSV-Feld-Zuordnung</h3>
                    
                    <div className={Styles.formGroup}>
                        <label>CSV-Beispieldatei hochladen *</label>
                        <div className={Styles.fileUploadContainer}>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleCsvUpload}
                                className={Styles.fileInput}
                                id="csvFileInput"
                            />
                            <label htmlFor="csvFileInput" className={Styles.fileUploadLabel}>
                                {csvFile ? `📄 ${csvFile.name}` : '📁 CSV-Datei auswählen'}
                            </label>
                            {csvFile && (
                                <button
                                    type="button"
                                    onClick={removeCsvFile}
                                    className={Styles.removeFileButton}
                                    title="Datei entfernen"
                                >
                                    ✕
                                </button>
                            )}
                            {!csvFile && csvHeaders.length > 0 && !isCreating && (
                                <div className={Styles.savedHeadersIndicator}>
                                    💾 Gespeicherte CSV-Struktur ({csvHeaders.length} Spalten)
                                </div>
                            )}
                        </div>
                        <p className={Styles.helpText}>
                            {csvHeaders.length > 0 && !csvFile && !isCreating
                                ? `Gespeicherte CSV-Struktur mit ${csvHeaders.length} Spalten wird verwendet. Laden Sie eine neue CSV-Datei hoch, um die Spalten zu aktualisieren.`
                                : 'Laden Sie eine CSV-Beispieldatei hoch, um die verfügbaren Spalten für das Mapping zu sehen.'
                            }
                        </p>
                    </div>

                    {/* CSV Validation Results */}
                    {csvValidationResults && (
                        <div className={Styles.validationSection}>
                            <h4>Datenvalidierung</h4>
                            <div className={Styles.validationResults}>
                                {csvValidationResults.errors.length > 0 && (
                                    <div className={Styles.validationErrors}>
                                        <h5>❌ Fehler ({csvValidationResults.errors.length})</h5>
                                        <ul>
                                            {csvValidationResults.errors.map((error, index) => (
                                                <li key={index}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {csvValidationResults.warnings.length > 0 && (
                                    <div className={Styles.validationWarnings}>
                                        <h5>⚠️ Warnungen ({csvValidationResults.warnings.length})</h5>
                                        <ul>
                                            {csvValidationResults.warnings.slice(0, 10).map((warning, index) => (
                                                <li key={index}>{warning}</li>
                                            ))}
                                            {csvValidationResults.warnings.length > 10 && (
                                                <li>... und {csvValidationResults.warnings.length - 10} weitere Warnungen</li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                                
                                {csvValidationResults.suggestions.length > 0 && (
                                    <div className={Styles.validationSuggestions}>
                                        <h5>💡 Hinweise ({csvValidationResults.suggestions.length})</h5>
                                        <ul>
                                            {csvValidationResults.suggestions.map((suggestion, index) => (
                                                <li key={index}>{suggestion}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                <div className={Styles.validationSummary}>
                                    Validiert: {csvValidationResults.totalRows} Datenzeilen (Stichprobe)
                                </div>
                            </div>
                        </div>
                    )}

                    {(csvHeaders.length > 0 || (!isCreating && Object.keys(formData.mapping).length > 0)) && (
                        <div className={Styles.mappingSection}>
                            <div className={Styles.mappingHeader}>
                                <h4>Feld-Zuordnung</h4>
                                <p className={Styles.mappingInfo}>
                                    {csvHeaders.length > 0 
                                        ? 'Ordnen Sie die CSV-Spalten den entsprechenden Shopware-Feldern zu. Nicht zugeordnete Felder werden beim Import ignoriert.'
                                        : 'Gespeicherte Feld-Zuordnungen aus diesem Profil. Laden Sie eine neue CSV-Datei hoch, um die Zuordnungen zu ändern.'
                                    }
                                </p>
                            </div>

                            {/* Field Mapping Tabs */}
                            <div className={Styles.mappingTabs}>
                                <button 
                                    className={`${Styles.tabButton} ${activeFieldTab === 'assigned' ? Styles.tabButtonActive : ''}`}
                                    onClick={() => setActiveFieldTab('assigned')}
                                >
                                    Zugeordnete Felder ({Object.values(formData.mapping).filter(field => field).length})
                                </button>
                                <button 
                                    className={`${Styles.tabButton} ${activeFieldTab === 'unassigned' ? Styles.tabButtonActive : ''}`}
                                    onClick={() => setActiveFieldTab('unassigned')}
                                >
                                    Nicht zugeordnete Felder ({csvHeaders.length > 0 ? csvHeaders.filter(header => !formData.mapping[header]).length : Object.keys(formData.mapping).filter(key => !formData.mapping[key]).length})
                                </button>
                            </div>
                            
                            <div className={Styles.mappingGrid}>
                                <div className={Styles.mappingGridHeader}>
                                    <span>CSV-Spalte</span>
                                    <span>Shopware-Feld</span>
                                </div>
                                
                                {(() => {
                                    const allHeaders = csvHeaders.length > 0 ? csvHeaders : Object.keys(formData.mapping);
                                    const filteredHeaders = activeFieldTab === 'assigned' 
                                        ? allHeaders.filter(header => formData.mapping[header])
                                        : allHeaders.filter(header => !formData.mapping[header]);
                                    
                                    return filteredHeaders.map((header, index) => {
                                        // Calculate z-index: much higher to ensure it's above everything
                                        const zIndex = 100000 - index;
                                        
                                        return (
                                            <div key={header} className={Styles.mappingRow}>
                                                <div className={Styles.csvColumn}>
                                                    <span className={Styles.csvColumnName}>{header}</span>
                                                    <span className={Styles.csvColumnIndex}>
                                                        {csvHeaders.length > 0 
                                                            ? `Spalte ${csvHeaders.indexOf(header) + 1}`
                                                            : 'Gespeicherte Zuordnung'
                                                        }
                                                    </span>
                                                </div>
                                                <div className={Styles.shopwareFieldSelector} style={{ position: 'relative', zIndex: zIndex }}>
                                                    <SearchableSelect
                                                        options={[
                                                            { id: '', name: 'Nicht zuordnen', category: '' },
                                                            ...orderFields.map(field => ({
                                                                id: field.value,
                                                                name: field.label,
                                                                category: field.category,
                                                                value: field.value
                                                            }))
                                                        ]}
                                                        value={formData.mapping[header] || ''}
                                                        onChange={(value) => handleMappingChange(header, value)}
                                                        placeholder="Shopware-Feld auswählen..."
                                                        searchPlaceholder="Feld suchen..."
                                                        className={Styles.mappingSelect}
                                                        zIndex={zIndex}
                                                        renderOption={(option) => (
                                                            <div className={Styles.fieldOption}>
                                                                <span className={Styles.fieldName}>{option.name}</span>
                                                                {option.category && (
                                                                    <span className={Styles.fieldCategory}>
                                                                        {option.category}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                                
                                {(() => {
                                    const allHeaders = csvHeaders.length > 0 ? csvHeaders : Object.keys(formData.mapping);
                                    const filteredHeaders = activeFieldTab === 'assigned' 
                                        ? allHeaders.filter(header => formData.mapping[header])
                                        : allHeaders.filter(header => !formData.mapping[header]);
                                    
                                    if (filteredHeaders.length === 0) {
                                        return (
                                            <div className={Styles.emptyTabMessage}>
                                                {activeFieldTab === 'assigned' 
                                                    ? 'Keine CSV-Felder zugeordnet. Wechseln Sie zum Tab "Nicht zugeordnete Felder", um Zuordnungen vorzunehmen.'
                                                    : 'Alle CSV-Felder sind bereits zugeordnet.'}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                            
                            <div className={Styles.mappingStats}>
                                <div className={Styles.statItem}>
                                    <span className={Styles.statLabel}>Zugeordnet:</span>
                                    <span className={Styles.statValue}>
                                        {Object.values(formData.mapping).filter(field => field).length}
                                    </span>
                                </div>
                                <div className={Styles.statItem}>
                                    <span className={Styles.statLabel}>Nicht zugeordnet:</span>
                                    <span className={Styles.statValue}>
                                        {csvHeaders.length > 0 
                                            ? csvHeaders.filter(header => !formData.mapping[header]).length 
                                            : Object.keys(formData.mapping).filter(key => !formData.mapping[key]).length
                                        }
                                    </span>
                                </div>
                                <div className={Styles.statItem}>
                                    <span className={Styles.statLabel}>Gesamt:</span>
                                    <span className={Styles.statValue}>
                                        {csvHeaders.length > 0 ? csvHeaders.length : Object.keys(formData.mapping).length}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className={Styles.actions}>
                    <button
                        className={Styles.cancelButton}
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        Abbrechen
                    </button>
                    <button
                        className={Styles.saveButton}
                        onClick={handleSave}
                        disabled={isLoading || !formData.profileName.trim()}
                    >
                        {isLoading ? 'Speichern...' : 'Profil speichern'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileEditor;